import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchPozivnica, savePozivnica, pozivnicaPdfEndpoint } from '../../../endpoint/index.ts'
import { IPozivnicaDogadjaj, PozivnicaStil } from '../../../interface/index.ts'
import './index.css'

interface Props {
	plannerId: string;
}

interface IDogadjajForm {
	naziv: string;
	mjesto: string;
	adresa: string;
	vrijeme: string; // vrijednost <input type="datetime-local">, lokalno vrijeme
}

// Native <input type=date> nema :placeholder-shown, pa se prazno polje ne da
// razlikovati od ispunjenog samim CSS-om. Bez toga "dd.mm.yyyy" izgleda kao
// upisan datum, a par ne vidi sto mu je jos ostalo.
const klasaDatuma = (vrijednost: string): string =>
	vrijednost ? 'pozivnica-datum' : 'pozivnica-datum pozivnica-datum-prazno'

const prazanDogadjaj = (): IDogadjajForm => ({ naziv: '', mjesto: '', adresa: '', vrijeme: '' })

// ISO string (UTC) -> vrijednost za <input type="datetime-local"> u lokalnom
// vremenu preglednika. Rezanje ISO stringa bi prikazalo UTC kao da je
// lokalno, pa vrijeme koje par unese ne bi bilo ono koje mu se kasnije vrati.
const naDatetimeLocal = (iso?: string): string => {
	if (!iso) return ''
	const d = new Date(iso)
	if (isNaN(d.getTime())) return ''
	const dva = (n: number) => String(n).padStart(2, '0')
	return `${d.getFullYear()}-${dva(d.getMonth() + 1)}-${dva(d.getDate())}T${dva(d.getHours())}:${dva(d.getMinutes())}`
}

// ISO string -> vrijednost za <input type="date">, isto lokalno pravilo.
const naDateInput = (iso?: string): string => {
	if (!iso) return ''
	const d = new Date(iso)
	if (isNaN(d.getTime())) return ''
	const dva = (n: number) => String(n).padStart(2, '0')
	return `${d.getFullYear()}-${dva(d.getMonth() + 1)}-${dva(d.getDate())}`
}

// Samo polja koja je par stvarno upisao putuju dalje - prazan objekt vraća
// undefined, pa spremanje ne izmišlja domjenak koji nitko nije dirnuo.
const izgradiDogadjaj = (d: IDogadjajForm): IPozivnicaDogadjaj | undefined => {
	const puno: IPozivnicaDogadjaj = {}
	if (d.naziv.trim()) puno.naziv = d.naziv.trim()
	if (d.mjesto.trim()) puno.mjesto = d.mjesto.trim()
	if (d.adresa.trim()) puno.adresa = d.adresa.trim()
	if (d.vrijeme) {
		const datum = new Date(d.vrijeme)
		if (!isNaN(datum.getTime())) puno.vrijeme = datum.toISOString()
	}
	return Object.keys(puno).length > 0 ? puno : undefined
}

const base64ToBlob = (base64: string, mime = 'application/pdf'): Blob => {
	const byteChars = atob(base64)
	const byteNumbers = new Array(byteChars.length)
	for (let i = 0; i < byteChars.length; i++) {
		byteNumbers[i] = byteChars.charCodeAt(i)
	}
	return new Blob([new Uint8Array(byteNumbers)], { type: mime })
}

const preuzmiDatoteku = (blob: Blob, naziv: string) => {
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = naziv
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}

const STILOVI: PozivnicaStil[] = ['klasik', 'moderno', 'rustik']

// Karticu ili A5 pozivnicu par piše na ovoj kartici planera, kao svoju petu
// (ne šestu) stvar - vlastita puna širina, ne dio gornjeg reda gdje traka za
// RSVP već stoji sklopljena da plan sale ne bi izgubio fokus.
// Raspored PDF-a reze poruku na sedam redaka da tekst ne prekrije QR. Najuzi
// od tri stila stane oko 40 znakova po retku, pa je ovo gornja granica koja
// prezivi svaki izbor stila. Bez nje bi par pisao dulje pismo, spremio ga bez
// upozorenja i vidio da je odrezano tek na tisku.
const MAX_PORUKA = 280

const PozivnicaPanel: React.FC<Props> = ({ plannerId }) => {
	const { t } = useTranslation('planner')

	const [obred, setObred] = useState<IDogadjajForm>(prazanDogadjaj())
	const [domjenak, setDomjenak] = useState<IDogadjajForm>(prazanDogadjaj())
	const [poruka, setPoruka] = useState('')
	const [rokPotvrde, setRokPotvrde] = useState('')
	// Stil nema "neodlučeno" u shemi - uvijek jedan od tri. Klasik kao zadani
	// izbor dok par ne odabere drugačije, ne prazno polje.
	const [stil, setStil] = useState<PozivnicaStil>('klasik')

	const [spremanje, setSpremanje] = useState(false)
	const [spremljeno, setSpremljeno] = useState(false)
	const [greskaSpremanja, setGreskaSpremanja] = useState(false)

	// Posluzitelj odbija generirati PDF dok dokument pozivnice ne postoji i vraca
	// HttpsError("not-found"), sto u pregledniku izgleda kao goli
	// "POST .../pozivnicaPdf 404 (Not Found)". Zato se preuzimanje otkljucava tek
	// kad znamo da ima sto preuzeti - par koji je tek otvorio karticu inace
	// pritisne gumb i dobije gresku koja mu ne kaze da prvo treba spremiti.
	const [postoji, setPostoji] = useState(false)

	const [preuzimanje, setPreuzimanje] = useState<'kartica' | 'pozivnica' | null>(null)
	const [greskaPreuzimanja, setGreskaPreuzimanja] = useState<'kartica' | 'pozivnica' | null>(null)

	// Sprema se samo ono što je stiglo prije nego se komponenta odmontira -
	// bez ovoga bi kasni odgovor mogao pozvati setState na već otišloj
	// komponenti (i, teoretski, prepisati ono što je par u međuvremenu upisao).
	const zivo = useRef(true)
	useEffect(() => () => { zivo.current = false }, [])

	useEffect(() => {
		fetchPozivnica(plannerId)
			.then((data) => {
				if (!zivo.current || !data) return
				setPostoji(true)
				if (data.obred) {
					setObred({
						naziv: data.obred.naziv || '',
						mjesto: data.obred.mjesto || '',
						adresa: data.obred.adresa || '',
						vrijeme: naDatetimeLocal(data.obred.vrijeme)
					})
				}
				if (data.domjenak) {
					setDomjenak({
						naziv: data.domjenak.naziv || '',
						mjesto: data.domjenak.mjesto || '',
						adresa: data.domjenak.adresa || '',
						vrijeme: naDatetimeLocal(data.domjenak.vrijeme)
					})
				}
				if (data.poruka !== undefined) setPoruka(data.poruka)
				if (data.rokPotvrde !== undefined) setRokPotvrde(naDateInput(data.rokPotvrde))
				if (data.stil) setStil(data.stil)
			})
			.catch(() => {
				// Ne ruši obrazac - par i dalje može upisivati i spremati iznova.
			})
	}, [plannerId])

	const handleSpremi = async () => {
		setSpremanje(true)
		setGreskaSpremanja(false)
		setSpremljeno(false)
		try {
			const payload: { obred?: IPozivnicaDogadjaj, domjenak?: IPozivnicaDogadjaj, poruka?: string, rokPotvrde?: string, stil: PozivnicaStil } = { stil }

			const obredPuno = izgradiDogadjaj(obred)
			if (obredPuno) payload.obred = obredPuno

			const domjenakPuno = izgradiDogadjaj(domjenak)
			if (domjenakPuno) payload.domjenak = domjenakPuno

			if (poruka.trim()) payload.poruka = poruka.trim()

			if (rokPotvrde) {
				const datum = new Date(rokPotvrde)
				if (!isNaN(datum.getTime())) payload.rokPotvrde = datum.toISOString()
			}

			const ok = await savePozivnica(plannerId, payload)
			if (zivo.current) {
				if (ok) {
					setSpremljeno(true)
					setPostoji(true)
				} else { setGreskaSpremanja(true) }
			}
		} catch {
			if (zivo.current) setGreskaSpremanja(true)
		} finally {
			if (zivo.current) setSpremanje(false)
		}
	}

	const handlePreuzmi = async (oblik: 'kartica' | 'pozivnica') => {
		setPreuzimanje(oblik)
		setGreskaPreuzimanja(null)
		try {
			const { pdfBase64, naziv } = await pozivnicaPdfEndpoint(plannerId, oblik)
			preuzmiDatoteku(base64ToBlob(pdfBase64), naziv)
		} catch {
			if (zivo.current) setGreskaPreuzimanja(oblik)
		} finally {
			if (zivo.current) setPreuzimanje(null)
		}
	}

	const azurirajObred = (polje: keyof IDogadjajForm, vrijednost: string) => {
		setObred((prev) => ({ ...prev, [polje]: vrijednost }))
		setSpremljeno(false)
	}

	const azurirajDomjenak = (polje: keyof IDogadjajForm, vrijednost: string) => {
		setDomjenak((prev) => ({ ...prev, [polje]: vrijednost }))
		setSpremljeno(false)
	}

	return (
		<div className='pozivnica-panel'>
			<h2 className='pozivnica-naslov'>{t('pozivnica.naslov')}</h2>
			<p className='pozivnica-podnaslov'>{t('pozivnica.podnaslov')}</p>

			<div className='pozivnica-dogadjaji'>
				<fieldset className='pozivnica-dogadjaj'>
					<legend>{t('pozivnica.obredNaslov')}</legend>

					<label className='pozivnica-polje' htmlFor='obred-naziv'>
						<span>{t('pozivnica.poljeNaziv')}</span>
						<input
							id='obred-naziv'
							data-testid='obred-naziv'
							value={obred.naziv}
							onChange={(e) => azurirajObred('naziv', e.target.value)}
						/>
					</label>
					<label className='pozivnica-polje' htmlFor='obred-mjesto'>
						<span>{t('pozivnica.poljeMjesto')}</span>
						<input
							id='obred-mjesto'
							data-testid='obred-mjesto'
							value={obred.mjesto}
							onChange={(e) => azurirajObred('mjesto', e.target.value)}
						/>
					</label>
					<label className='pozivnica-polje' htmlFor='obred-adresa'>
						<span>{t('pozivnica.poljeAdresa')}</span>
						<input
							id='obred-adresa'
							data-testid='obred-adresa'
							value={obred.adresa}
							onChange={(e) => azurirajObred('adresa', e.target.value)}
						/>
					</label>
					<label className='pozivnica-polje' htmlFor='obred-vrijeme'>
						<span>{t('pozivnica.poljeVrijeme')}</span>
						<input
							id='obred-vrijeme'
							data-testid='obred-vrijeme'
							type='datetime-local'
							className={klasaDatuma(obred.vrijeme)}
							value={obred.vrijeme}
							onChange={(e) => azurirajObred('vrijeme', e.target.value)}
						/>
					</label>
				</fieldset>

				<fieldset className='pozivnica-dogadjaj'>
					<legend>{t('pozivnica.domjenakNaslov')}</legend>

					<label className='pozivnica-polje' htmlFor='domjenak-naziv'>
						<span>{t('pozivnica.poljeNaziv')}</span>
						<input
							id='domjenak-naziv'
							data-testid='domjenak-naziv'
							value={domjenak.naziv}
							onChange={(e) => azurirajDomjenak('naziv', e.target.value)}
						/>
					</label>
					<label className='pozivnica-polje' htmlFor='domjenak-mjesto'>
						<span>{t('pozivnica.poljeMjesto')}</span>
						<input
							id='domjenak-mjesto'
							data-testid='domjenak-mjesto'
							value={domjenak.mjesto}
							onChange={(e) => azurirajDomjenak('mjesto', e.target.value)}
						/>
					</label>
					<label className='pozivnica-polje' htmlFor='domjenak-adresa'>
						<span>{t('pozivnica.poljeAdresa')}</span>
						<input
							id='domjenak-adresa'
							data-testid='domjenak-adresa'
							value={domjenak.adresa}
							onChange={(e) => azurirajDomjenak('adresa', e.target.value)}
						/>
					</label>
					<label className='pozivnica-polje' htmlFor='domjenak-vrijeme'>
						<span>{t('pozivnica.poljeVrijeme')}</span>
						<input
							id='domjenak-vrijeme'
							data-testid='domjenak-vrijeme'
							type='datetime-local'
							className={klasaDatuma(domjenak.vrijeme)}
							value={domjenak.vrijeme}
							onChange={(e) => azurirajDomjenak('vrijeme', e.target.value)}
						/>
					</label>
				</fieldset>
			</div>

			<label className='pozivnica-polje' htmlFor='pozivnica-poruka'>
				<span>{t('pozivnica.poruka')}</span>
				<textarea
					id='pozivnica-poruka'
					data-testid='pozivnica-poruka'
					rows={3}
					maxLength={MAX_PORUKA}
					placeholder={t('pozivnica.porukaPlaceholder')}
					value={poruka}
					onChange={(e) => { setPoruka(e.target.value); setSpremljeno(false) }}
				/>
				{/* Dva gola broja, ne recenica: "jos 12 znakova" trazi tri oblika u
				    hrvatskom, srpskom, bosanskom i slovenskom. */}
				<span className='pozivnica-brojac' data-testid='pozivnica-poruka-brojac'>
					{poruka.length} / {MAX_PORUKA}
				</span>
			</label>

			<label className='pozivnica-polje pozivnica-rok' htmlFor='pozivnica-rok'>
				<span>{t('pozivnica.rokPotvrde')}</span>
				<input
					id='pozivnica-rok'
					data-testid='pozivnica-rok'
					type='date'
					className={klasaDatuma(rokPotvrde)}
					value={rokPotvrde}
					onChange={(e) => { setRokPotvrde(e.target.value); setSpremljeno(false) }}
				/>
			</label>

			<fieldset className='pozivnica-stil'>
				<legend>{t('pozivnica.stilNaslov')}</legend>
				<div className='pozivnica-stil-mreza'>
					{STILOVI.map((opcija) => (
						<label
							key={opcija}
							className={`pozivnica-stil-karta pozivnica-stil-karta-${opcija} ${stil === opcija ? 'odabrano' : ''}`}
						>
							<input
								type='radio'
								name='pozivnica-stil'
								data-testid={`stil-${opcija}`}
								checked={stil === opcija}
								onChange={() => { setStil(opcija); setSpremljeno(false) }}
							/>
							<span className='pozivnica-stil-uzorak' aria-hidden='true'>
								<span className='pozivnica-stil-uzorak-imena'>Ana &amp; Ivan</span>
							</span>
							<span className='pozivnica-stil-ime'>{t(`pozivnica.stil${opcija.charAt(0).toUpperCase()}${opcija.slice(1)}Ime`)}</span>
							<span className='pozivnica-stil-opis'>{t(`pozivnica.stil${opcija.charAt(0).toUpperCase()}${opcija.slice(1)}Opis`)}</span>
						</label>
					))}
				</div>
			</fieldset>

			<div className='pozivnica-akcije'>
				<button
					type='button'
					className='pozivnica-spremi-btn'
					disabled={spremanje}
					onClick={handleSpremi}
				>
					{spremanje ? t('pozivnica.spremanje') : t('pozivnica.spremi')}
				</button>
				{spremljeno && <span className='pozivnica-status' role='status'>{t('pozivnica.spremljeno')}</span>}
				{greskaSpremanja && <span className='pozivnica-greska' role='alert'>{t('pozivnica.greskaSpremanja')}</span>}
			</div>

			<div className='pozivnica-preuzimanje'>
				<h3>{t('pozivnica.preuzmiNaslov')}</h3>
				{!postoji && (
					<p className='pozivnica-preuzmi-uputa' data-testid='pozivnica-preuzmi-uputa'>
						{t('pozivnica.preuzmiUputa')}
					</p>
				)}
				<div className='pozivnica-preuzmi-gumbi'>
					<button
						type='button'
						className='pozivnica-preuzmi-btn'
						disabled={!postoji || preuzimanje === 'kartica'}
						onClick={() => handlePreuzmi('kartica')}
					>
						{preuzimanje === 'kartica' ? t('pozivnica.preuzimanje') : t('pozivnica.preuzmiKartica')}
					</button>
					<button
						type='button'
						className='pozivnica-preuzmi-btn'
						disabled={!postoji || preuzimanje === 'pozivnica'}
						onClick={() => handlePreuzmi('pozivnica')}
					>
						{preuzimanje === 'pozivnica' ? t('pozivnica.preuzimanje') : t('pozivnica.preuzmiPozivnica')}
					</button>
				</div>
				{greskaPreuzimanja && (
					<p className='pozivnica-greska' role='alert'>{t('pozivnica.preuzmiGreska')}</p>
				)}
			</div>
		</div>
	)
}

export default PozivnicaPanel
